import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import BrokerEditOfferPage from 'pages/AdvertManagementPage/BrokerEditOfferPage';
import AnalyticPanelPage from 'pages/AnalyticPanelPage.tsx';
import BrokerOffersPage from 'pages/BrokerOffersPage';
import CalculatorPage from 'pages/CalculatorPage.tsx';
import CalculatorSettingsPage from 'pages/CalculatorSettingsPage';
import ChatbotMessagesPage from 'pages/ChatbotMessagesPage.tsx';
import CooperativePage from 'pages/CooperaivePage';
import ModuleUnavailablePage from 'pages/ModuleUnavailblePage.tsx';
import PropertyValuationPage from 'pages/PropertyValuationPage.tsx';
import CreateAccountAndPurchaseAppPage from 'pages/PurchaseProductPage/CreateAccountAndPurchaseAppPage.tsx';
import Przelewy24Page from 'pages/PurchaseProductPage/Przelewy24Page.tsx';
import PurchaseProductPage from 'pages/PurchaseProductPage/PurchaseProductPage.tsx';
import SingleCooperativeDetailPage from 'pages/SingleDetailsPages/SingleCooperativeDetailPage';
import styled from 'styled-components';
import { AnalyticPanelProvider } from 'utils/context/AnalyticPanelContext.tsx';
import { CalculatorModuleProvider } from 'utils/context/CalculatorModuleContext.tsx';
import { CooperativeProvider } from 'utils/context/CooperativeContext';
import { ProductPurchaseProvider } from 'utils/context/ProductPurchaseContext.tsx';
import { PropertyValuationProvider } from 'utils/context/PropertyValuationContext.tsx';
import { RentProvider } from 'utils/context/RentContext';
import { logData } from 'utils/helpers/logData.tsx';
import { Platform, usePlatform } from 'utils/hooks/usePlatform.tsx';
import { isProductionEnvironment } from 'utils/isProductionEnvironment.ts';
import { hasRole, UserRole } from 'utils/userRoleHelper.ts';
import { useWindowWidth } from 'utils/useWindowWidth';

import ChatbotMessages from 'components/common/Chatbot/ChatbotMessages.tsx';
import ResourcePopup from 'components/common/ResourcePopUp.tsx';
import NavigationDesktop from 'components/NavigationDesktop/Navigation.tsx';
import NavigationMobile, { type TabId } from 'components/NavigationMobile/NavigationMobile.tsx';
import UpdateNotificationBar from 'components/UpdateNotificationBar.tsx';
import VersionAppPopup from 'components/VersionAppPopUp.tsx';

import { getToken, messaging, onMessage } from './features/firebase/firebaseConfig.ts';
import AdvertManagementPage from './pages/AdvertManagementPage/AdvertManagementPage';
import AnalysisPage from './pages/AnalysisPage';
import AuctionPage from './pages/AuctionPage';
import BailiffPage from './pages/BailiffPage';
import FavoritesPage from './pages/FavoritesPage';
import MyOffersPage from './pages/MyOffersPage';
import MySettingsPage from './pages/MySettingsPage/MySettingsPage.tsx';
import OwnerProfilePage from './pages/OwnerProfilePage';
import RentPage from './pages/RentPage';
import SalesPage from './pages/SalesPage';
import SingleAuctionDetailPage from './pages/SingleDetailsPages/SingleAuctionDetailPage';
import SingleBailiffDetailPage from './pages/SingleDetailsPages/SingleBailiffDetailPage';
import SingleRentDetailPage from './pages/SingleDetailsPages/SingleRentDetailPage';
import SingleSaleDetailPage from './pages/SingleDetailsPages/SingleSaleDetailPage';
import SingleTenderDetailPage from './pages/SingleDetailsPages/SingleTenderDetailPage';
import TenderPage from './pages/TenderPage';
import UserAddAdvertPage from './pages/UserAddAdvertPage';
import UserAdvertsPage from './pages/UserAdvertsPage';
import UserEditAdvertPage from './pages/UserEditAdvertPage';
import UserMessagesPage from './pages/UserMessagesPage';
import UserOffersMiniPage from './pages/UserOffersMiniPage';
import { hasValidSubscription, useUser } from './store/User/Context';
import { userApiRequests } from './utils/api-requests/user.tsx';
import { AnalysisSaleProvider } from './utils/context/AnalysisSaleContext';
import { AuctionProvider } from './utils/context/AuctionContext';
import { BailiffProvider } from './utils/context/BailiffContext';
import { BrokerAdvertProvider } from './utils/context/BrokerAdvertContext';
import { SaleProvider } from './utils/context/SaleContext';
import { TenderProvider } from './utils/context/TenderContext';
import { UserAddAdvertProvider } from './utils/context/UserAddAdvertContext';
import { Packages } from './utils/enums/packages';
import { errorFallback } from './utils/errorFallback.tsx';

import 'react-notifications-component/dist/theme.css';

const AddPagesForm = React.lazy(() => import('./components/adminForm/AddPagesForm'));

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: 72px;

    @media (min-width: 1100px) {
        padding-bottom: 0;
        width: calc(100% - var(--nav-desktop-width, 76px));
        margin-left: var(--nav-desktop-width, 76px);
        transition: width 0.28s ease, margin-left 0.28s ease;
    }
`;

const MainContainer = styled.div<{ isAndroid: boolean }>`
    display: flex;
    flex-direction: column;
    max-width: 1600px;
    margin: auto;
    position: relative;
    margin-top: ${({ isAndroid }) => isAndroid ? '30px' : '0'};
    padding-bottom: 50px;
    min-height: 100vh;
    background-color: var(--color-alt-second);
`;

const App: FC = () => {
    const { user } = useUser();
    const [showUpdateNotification, setShowUpdateNotification] = useState(false);
    const currentVersionRef = useRef<string | null>(null);
    const validSubscription = hasValidSubscription(user.userRole);
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);
    const windowWidth = useWindowWidth();
    const isDesktop = windowWidth >= 1100;
    const [mobileTab, setMobileTab] = useState<TabId | null>(null);

    const isDesktopRef = useRef(isDesktop);
    isDesktopRef.current = isDesktop;
    const mobileTabRef = useRef(mobileTab);
    mobileTabRef.current = mobileTab;

    const platform = usePlatform();
    const isAndroid = platform === Platform.Android;

    const [isOpenPopUp, setIsOpenPopUp] = useState(false);
    const [availableResource, setAvailiabaleResource] = useState<{ smsCount: number | null, tokenCount: number | null }>({ smsCount: null, tokenCount: null });

    const checkResources = async () => {
        const data = await userApiRequests.getUserResourceInfo();
        if (!data) return;

        const { totalAvailableSms: smsCount, totalAvailableAiTokens: tokenCount } = data;
        setAvailiabaleResource({ smsCount, tokenCount });

        if (smsCount <= 10 && !localStorage.getItem('smsInfoShown')) {
            setIsOpenPopUp(true);
            localStorage.setItem('smsInfoShown', 'true');
        }

        if (tokenCount <= 50000 && !localStorage.getItem('tokenInfoShown')) {
            setIsOpenPopUp(true);
            localStorage.setItem('tokenInfoShown', 'true');
        }
    };

    useEffect(() => {
        if (platform !== Platform.IOS) {
            checkResources();
            const interval = setInterval(checkResources, 24 * 60 * 60 * 1000);

            return () => clearInterval(interval);
        }
    }, []);

    const checkVersion = () => {
        fetch(`/version.json?t=${new Date().getTime()}`)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 404) {
                        // eslint-disable-next-line no-console
                        console.log('Plik version.json nie istnieje - przeładowanie aplikacji');
                        window.location.reload();

                        return null;
                    }
                    throw new Error(`Błąd podczas pobierania version.json: ${response.status}`);
                }

                return response.json();
            })
            .then((data) => {
                if (!data) return;

                if (!currentVersionRef.current) {
                    currentVersionRef.current = data.buildTimestamp;
                    // eslint-disable-next-line no-console
                    console.log('Inicjalizacja wersji aplikacji:', data.buildTimestamp);

                    return;
                }

                if (data.buildTimestamp !== currentVersionRef.current) {
                    // eslint-disable-next-line no-console
                    console.log('Wykryto nową wersję:', data);
                    setShowUpdateNotification(true);
                }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Błąd podczas sprawdzania wersji:', error);
            });
    };

    interface BrowserInfo {
        name: string;
        version: string;
        platform: string;
    }

    const getBrowserInfo = (): BrowserInfo => {
        const rawUA = navigator.userAgent;
        const match = rawUA.match(/(Firefox|Chrome|Safari|Edg|Opera|OPR)\/([\d.]+)/);

        return {
            name: match?.[1] || 'Unknown',
            version: match?.[2] || 'Unknown',
            platform: navigator.platform || 'Unknown'
        };
    };

    const logBrowserInfo = () => {
        if (!isProductionEnvironment()) return;

        const browserInfo = getBrowserInfo();

        return logData(browserInfo);
    };

    useEffect(() => {
        checkVersion();
        logBrowserInfo();
        const interval = setInterval(checkVersion, 60 * 60 * 1000);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', checkVersion);
        };
    }, []);

    const closeNotification = () => {
        setShowUpdateNotification(false);
    };
    const location = useLocation();

    const shouldHideChatbot = (pathname: string): boolean => {
        const hiddenPaths = [
            '/chatbot-messages',
            '/przelewy24',
            '/payment/success',
            '/payment/failure',
            '/users-offers-mini'
        ];

        if (hiddenPaths.includes(pathname)) {
            return true;
        }

        if (pathname.startsWith('/payment/')) {
            return true;
        }

        if (pathname.includes('/edit')) {
            return true;
        }

        return false;
    };

    const isIos = () => {
        const userAgent = window.navigator.userAgent.toLowerCase();

        return (/iphone|ipad|ipod/).test(userAgent);
    };

    useEffect(() => {
        if (isIos()) {
            // eslint-disable-next-line no-console
            console.log('Push notifications pominięte na iOS Safari.');

            return;
        }

        if ('serviceWorker' in navigator) {
            // eslint-disable-next-line no-console
            console.log('Service Worker dostępny.');
        }

        if (!messaging) return;

        Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
                getToken(messaging, { vapidKey: process.env.REACT_APP_VAPID_KEY })
                    .then(async (currentToken: string) => {
                        if (currentToken) {
                            await userApiRequests.sendPushNotificationToken(currentToken);
                        } else {
                            // eslint-disable-next-line no-console
                            console.log('Nie udało się uzyskać tokenu.');
                        }
                    })
                    .catch((err: string) => {
                        // eslint-disable-next-line no-console
                        console.error('Błąd podczas pobierania tokenu:', err);
                    });
            } else {
                // eslint-disable-next-line no-console
                console.log('Uprawnienia do powiadomień nie zostały przyznane.');
            }
        });

        onMessage(messaging, (payload: { data: { url: string; title: string; body: string; image: string; icon: string} }) => {
            if (Notification.permission === 'granted') {
                const { title, body, image, icon, url } = payload.data;

                new Notification(title, {
                    body,
                    icon,
                    image,
                    data: { link: url }
                } as NotificationOptions);
            }
        });
    }, []);

    const MainWrapper = useMemo(() => ({ children }: { children: ReactElement }) => {
        const { user: currentUser } = useUser();

        useEffect(() => {
            document.body.classList.remove('light', 'dark');
            document.body.classList.add(currentUser.isDarkMode ? 'dark' : 'light');
        }, [currentUser.isDarkMode]);

        return (
            <>
                {isDesktopRef.current ? <NavigationDesktop /> : <NavigationMobile activeTab={mobileTabRef.current} setActiveTab={setMobileTab} />}
                <ContentContainer>
                    {children}
                </ContentContainer>
            </>
        );
    }, []);

    return (
        <MainContainer isAndroid={isAndroid}>
            <Routes>
                <Route
                    path="/users-offers-mini"
                    element={
                        <React.Suspense fallback={<>...</>}>
                            <ErrorBoundary FallbackComponent={errorFallback}>
                                <UserOffersMiniPage/>
                            </ErrorBoundary>
                        </React.Suspense>
                    }
                />
                <Route
                    path="/przelewy24"
                    element={
                        <ErrorBoundary FallbackComponent={errorFallback}>
                            <Przelewy24Page />
                        </ErrorBoundary>
                    }
                />
                <Route
                    path="/favorites"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ulubione" />
                                    : <CooperativeProvider>
                                        <AuctionProvider>
                                            <TenderProvider>
                                                <BailiffProvider>
                                                    <RentProvider>
                                                        <SaleProvider>
                                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                                <FavoritesPage/>
                                                            </ErrorBoundary>
                                                        </SaleProvider>
                                                    </RentProvider>
                                                </BailiffProvider>
                                            </TenderProvider>
                                        </AuctionProvider>
                                    </CooperativeProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/calculator"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <CalculatorModuleProvider>
                                            <CalculatorPage />
                                        </CalculatorModuleProvider>
                                    </ErrorBoundary>
                                }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/analytic-panel"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Panel Analityczny" />
                                    : !hasCalculationRole
                                        ? <Navigate to="/" replace />
                                        : <ErrorBoundary FallbackComponent={errorFallback}>
                                            <AnalyticPanelProvider>
                                                <AnalyticPanelPage />
                                            </AnalyticPanelProvider>
                                        </ErrorBoundary>
                                }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/my-settings"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                <ErrorBoundary FallbackComponent={errorFallback}>
                                    <MySettingsPage/>
                                </ErrorBoundary>
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/calculator-settings"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Kalkulator" />
                                    : !hasCalculationRole
                                        ? <Navigate to="/" replace />
                                        : <ErrorBoundary FallbackComponent={errorFallback}>
                                            <CalculatorSettingsPage />
                                        </ErrorBoundary>
                                }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/broker"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Pośrednika" />
                                    : <BrokerAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <BrokerOffersPage/>
                                        </ErrorBoundary>
                                    </BrokerAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/user"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <UserAdvertsPage type={'users'}/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/add-offer"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <UserAddAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <UserAddAdvertPage/>
                                        </ErrorBoundary>
                                    </UserAddAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/my-offers"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <UserAddAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <MyOffersPage/>
                                        </ErrorBoundary>
                                    </UserAddAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/messages"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <UserMessagesPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/profile/:id"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <OwnerProfilePage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/edit-offer"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Ogłoszenia Użytkowników" />
                                    : <UserAddAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <UserEditAdvertPage/>
                                        </ErrorBoundary>
                                    </UserAddAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : user.activePackages.includes(Packages.ROLE_ADVERTISEMENT)
                                        ? <SaleProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <SalesPage/>
                                            </ErrorBoundary>
                                        </SaleProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : user.activePackages.includes(Packages.ROLE_ADVERTISEMENT)
                                        ? <SaleProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <SalesPage/>
                                            </ErrorBoundary>
                                        </SaleProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />

                <Route
                    path="/tender"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi" />
                                    : user.activePackages?.includes(Packages.ROLE_AUCTION)
                                        ? <TenderProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <TenderPage/>
                                            </ErrorBoundary>
                                        </TenderProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/tender/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi" />
                                    : user.activePackages?.includes(Packages.ROLE_AUCTION)
                                        ? <TenderProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <TenderPage/>
                                            </ErrorBoundary>
                                        </TenderProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/auction"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Licytacje Komornicze" />
                                    : user.activePackages?.includes(Packages.ROLE_AUCTION)
                                        ? <AuctionProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <AuctionPage/>
                                            </ErrorBoundary>
                                        </AuctionProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/auction/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Licytacje Komornicze" />
                                    : user.activePackages?.includes(Packages.ROLE_AUCTION)
                                        ? <AuctionProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <AuctionPage/>
                                            </ErrorBoundary>
                                        </AuctionProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/bailiff-notices"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Informacje o Zadłużeniach" />
                                    : user.activePackages?.includes(Packages.ROLE_BAILIFFNOTICE)
                                        ? <BailiffProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <BailiffPage/>
                                            </ErrorBoundary>
                                        </BailiffProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />

                <Route
                    path="/bailiff-notices/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Informacje o Zadłużeniach" />
                                    : user.activePackages?.includes(Packages.ROLE_BAILIFFNOTICE)
                                        ? <BailiffProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <BailiffPage/>
                                            </ErrorBoundary>
                                        </BailiffProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/cooperative"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi Lokalne" />
                                    : user.activePackages?.includes(Packages.ROLE_COOPERATIVE)
                                        ? <CooperativeProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <CooperativePage/>
                                            </ErrorBoundary>
                                        </CooperativeProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/cooperative/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi Lokalne" />
                                    : user.activePackages?.includes(Packages.ROLE_COOPERATIVE)
                                        ? <CooperativeProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <CooperativePage/>
                                            </ErrorBoundary>
                                        </CooperativeProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/analysis/:filterData?"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi" />
                                    : user.activePackages?.includes(Packages.ROLE_ANALYSIS)
                                        ? <AnalysisSaleProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <AnalysisPage/>
                                            </ErrorBoundary>
                                        </AnalysisSaleProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/analysis/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi" />
                                    : user.activePackages?.includes(Packages.ROLE_ANALYSIS)
                                        ? <AnalysisSaleProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <AnalysisPage/>
                                            </ErrorBoundary>
                                        </AnalysisSaleProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/rent/ai-filter/:aiFilterData"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Wynajem" />
                                    : user.activePackages?.includes(Packages.ROLE_RENT)
                                        ? <RentProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <RentPage/>
                                            </ErrorBoundary>
                                        </RentProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/rent"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Wynajem" />
                                    : user.activePackages?.includes(Packages.ROLE_RENT)
                                        ? <RentProvider>
                                            <ErrorBoundary FallbackComponent={errorFallback}>
                                                <RentPage/>
                                            </ErrorBoundary>
                                        </RentProvider>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/sale/:module/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleSaleDetailPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/rent/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Wynajem" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleRentDetailPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/bailiff-notice/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Informacje o zadłuzeniach" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleBailiffDetailPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/tender/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleTenderDetailPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/auction/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Licytacje Komornicze" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleAuctionDetailPage/>
                                    </ErrorBoundary> }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/cooperative/:hash"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Przetargi Lokalne" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <SingleCooperativeDetailPage/>
                                    </ErrorBoundary> }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/:type/:subtype/:id/copy"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="" />
                                    : <BrokerAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <AdvertManagementPage />
                                        </ErrorBoundary>
                                    </BrokerAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/broker/:type/:subtype/:id/copy"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="" />
                                    : <BrokerAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <AdvertManagementPage/>
                                        </ErrorBoundary>
                                    </BrokerAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/:type/:subtype/:id/edit"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="" />
                                    : <BrokerAdvertProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <BrokerEditOfferPage/>
                                        </ErrorBoundary>
                                    </BrokerAdvertProvider>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/chatbot-messages"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Wiadomości Flipchat" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <ChatbotMessagesPage/>
                                    </ErrorBoundary>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/admin-form"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : user.activePackages?.includes(Packages.ROLE_ADMIN)
                                        ? <ErrorBoundary FallbackComponent={errorFallback}>
                                            <AddPagesForm/>
                                        </ErrorBoundary>
                                        : <Navigate to={{ pathname: '/' }}/>}
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/purchase/:product"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Sprzedaż" />
                                    : <ProductPurchaseProvider>
                                        <ErrorBoundary FallbackComponent={errorFallback}>
                                            <PurchaseProductPage />
                                        </ErrorBoundary>
                                    </ProductPurchaseProvider>
                                }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/create-account-purchase-app"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                <ErrorBoundary FallbackComponent={errorFallback}>
                                    <CreateAccountAndPurchaseAppPage />
                                </ErrorBoundary>
                            </React.Suspense>
                        </MainWrapper>
                    }
                />
                <Route
                    path="/property-valuation"
                    element={
                        <MainWrapper>
                            <React.Suspense fallback={<>...</>}>
                                {!validSubscription
                                    ? <ModuleUnavailablePage moduleName="Wycena Mieszkań" />
                                    : <ErrorBoundary FallbackComponent={errorFallback}>
                                        <PropertyValuationProvider>
                                            <PropertyValuationPage/>
                                        </PropertyValuationProvider>
                                    </ErrorBoundary>
                                }
                            </React.Suspense>
                        </MainWrapper>
                    }
                />

            </Routes>

            {user.activePackages && validSubscription && !shouldHideChatbot(location.pathname) &&
                <ErrorBoundary FallbackComponent={errorFallback}>
                    <ChatbotMessages/>
                </ErrorBoundary>
            }

            {showUpdateNotification && platform === Platform.Web && <UpdateNotificationBar
                refreshPage={() => window.location.reload()}
                closeNotification={closeNotification} />
            }

            {showUpdateNotification && platform !== Platform.Web && <VersionAppPopup
                isOpen={showUpdateNotification}
                platform={platform}
                // jeśli trzeba to zmieniamy na isCritical na true podczas deploymentu na android/ios
                isCritical={false}
                onClose={closeNotification}/>
            }

            {platform !== Platform.IOS && <ResourcePopup
                isOpen={isOpenPopUp}
                availableResource={availableResource}
                onClose={() => setIsOpenPopUp(false)} />}
        </MainContainer>
    );
};

export default App;
